import { ReactComponent as LockIcon } from 'assets/icons/contextmenu_lock.svg';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as CutIcon } from 'assets/icons/cut.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as DuplicateIcon } from 'assets/icons/duplicate.svg';
import { ReactComponent as GroupIcon } from 'assets/icons/group.svg';
import { ReactComponent as PasteIcon } from 'assets/icons/paste.svg';
import { ReactComponent as MoveToBackIcon } from 'assets/icons/toolbar/layer/moveBack.svg';
import { ReactComponent as MoveBackwardsIcon } from 'assets/icons/toolbar/layer/moveBackwards.svg';
import { ReactComponent as MoveForwardIcon } from 'assets/icons/toolbar/layer/moveForwards.svg';
import { ReactComponent as MoveToFrontIcon } from 'assets/icons/toolbar/layer/moveFront.svg';
import {
  checkIsMoveBackwardAllowed,
  checkIsMoveForwardAllowed,
  copyActiveWidgetsToClipboard,
  duplicateActiveWidgets,
  groupActiveWidgets,
  moveWidgetByDirection,
  pasteWidgetsToCanvas,
  unGroupActiveWidget,
} from 'modules/Editor/Editor.helpers';
import { RootState, setActiveWidget, store } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { removeWidget, updateWidget } from 'modules/Editor/store/infographSlice';
import { WidgetDirection } from 'modules/Editor/store/infographSlice.types';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { selectWidgetClipboard } from 'modules/Editor/store/widgetSelector';
import { getLocalStorageItem } from 'utils/localStorage';

import { COPY_WIDGET_LOCAL_STORAGE_KEY } from '../Clipboard/Clipboard.config';
import { LocalStorageClipboardWidgets } from '../Clipboard/Clipboard.types';
import { ContextMenuItem } from './ContextMenu.types';

// Actions used in multiple context menus
const COMMON_ACTIONS: { [key: string]: ContextMenuItem } = {
  Paste: {
    id: 'paste',
    onClick: pasteWidgetsToCanvas,
    label: 'paste',
    checkIsDisabled: () => {
      // Check local storage
      const copyWidgetsFromLS = getLocalStorageItem(COPY_WIDGET_LOCAL_STORAGE_KEY) as LocalStorageClipboardWidgets;
      if (copyWidgetsFromLS?.copyWidgets?.length) return false;

      // If nothing in local storage, check clipboard
      const storeState = store.getState() as RootState;
      const widgetClipboard = selectWidgetClipboard(storeState);
      if (widgetClipboard?.length) return false;

      // Nothing has been copied, disable paste
      return true;
    },
    icon: <PasteIcon />,
    shortcuts: {
      macOs: ['⌘', '+', 'V'],
      others: ['ctrl', '+', 'V'],
    },
  },
};

// Common actions when right clicking on a widget selection
// Can be single or multi select
const WIDGET_ACTIONS: ContextMenuItem[] = [
  {
    id: 'cut',
    onClick: ({ activeWidgetIds, globalDispatch }) => {
      if (!globalDispatch) return;
      copyActiveWidgetsToClipboard();

      const storeState = store.getState() as RootState;
      const activePageId = selectActivePage(storeState);
      globalDispatch(removeWidget({ pageId: activePageId, widgetIds: activeWidgetIds }));
      globalDispatch(setActiveWidget([]));

      navigator.clipboard.writeText('');
    },
    label: 'cut',
    icon: <CutIcon />,
    shortcuts: {
      macOs: ['⌘', '+', 'X'],
      others: ['ctrl', '+', 'X'],
    },
  },
  {
    id: 'copy',
    onClick: () => {
      copyActiveWidgetsToClipboard();
      navigator.clipboard.writeText('');
    },
    label: 'copy',
    icon: <CopyIcon />,
    shortcuts: {
      macOs: ['⌘', '+', 'C'],
      others: ['ctrl', '+', 'C'],
    },
  },
  COMMON_ACTIONS.Paste,
  {
    id: 'duplicate',
    onClick: duplicateActiveWidgets,
    label: 'duplicate',
    icon: <DuplicateIcon />,
    shortcuts: {
      macOs: ['⌘', '+', 'D'],
      others: ['ctrl', '+', 'D'],
    },
  },
  {
    id: 'delete',
    onClick: ({ activeWidgetIds, globalDispatch }) => {
      if (!globalDispatch) return;

      const storeState = store.getState() as RootState;
      const activePageId = selectActivePage(storeState);
      globalDispatch(removeWidget({ pageId: activePageId, widgetIds: activeWidgetIds }));
      globalDispatch(setActiveWidget([]));
    },
    label: 'delete',
    icon: <DeleteIcon />,
    shortcuts: {
      macOs: ['delete'],
      others: ['delete'],
    },
  },
  {
    id: 'move-to-front',
    onClick: () => moveWidgetByDirection(WidgetDirection.Front),
    label: 'move-to-front',
    icon: <MoveToFrontIcon />,
    shortcuts: {
      macOs: ['ctrl', '+', 'shift', '+', '>'],
      others: ['ctrl', '+', 'shift', '+', '>'],
    },
    hasDivider: true,

    // Disabled if already at front
    checkIsDisabled: ({ activeWidgetIds }) => !checkIsMoveForwardAllowed(activeWidgetIds),
  },
  {
    id: 'move-forwards',
    onClick: () => moveWidgetByDirection(WidgetDirection.Up),
    label: 'move-forwards',

    // Disabled if already at front
    checkIsDisabled: ({ activeWidgetIds }) => !checkIsMoveForwardAllowed(activeWidgetIds),

    icon: <MoveForwardIcon />,
    shortcuts: {
      macOs: ['ctrl', '+', '>'],
      others: ['ctrl', '+', '>'],
    },
  },
  {
    id: 'move-backwards',
    onClick: () => moveWidgetByDirection(WidgetDirection.Down),
    label: 'move-backwards',

    // Disabled if already at back
    checkIsDisabled: ({ activeWidgetIds }) => !checkIsMoveBackwardAllowed(activeWidgetIds),

    icon: <MoveBackwardsIcon />,
    shortcuts: {
      macOs: ['ctrl', '+', '<'],
      others: ['ctrl', '+', '<'],
    },
  },
  {
    id: 'move-to-back',
    onClick: () => moveWidgetByDirection(WidgetDirection.Back),
    label: 'move-to-back',

    icon: <MoveToBackIcon />,
    shortcuts: {
      macOs: ['ctrl', '+', 'shift', '+', '<'],
      others: ['ctrl', '+', 'shift', '+', '<'],
    },

    // Disabled if already at back
    checkIsDisabled: ({ activeWidgetIds }) => !checkIsMoveBackwardAllowed(activeWidgetIds),
  },
  {
    id: 'lock',
    onClick: ({ globalDispatch, activeWidgetIds }) => {
      if (!globalDispatch) return;
      // can only lock single non-group/responsive widgets
      if (!activeWidgetIds || activeWidgetIds.length > 1) return;

      globalDispatch(updateWidget({ widgetId: activeWidgetIds[0], widgetData: { isLocked: true } }));
    },
    label: 'lock',
    checkIsDisabled: ({ activeWidgetIds }) => {
      return !activeWidgetIds || activeWidgetIds.length > 1;
    },
    checkIsHidden: ({ activeWidgetIds }) => {
      // Never hidden for multiselect + group/reponsive widgets - will be disabled by checkIsDisabled fn
      if (!activeWidgetIds || activeWidgetIds.length > 1) return false;

      const storeState = store.getState() as RootState;
      const widget = selectWidget(activeWidgetIds[0])(storeState);
      return !!widget?.isLocked;
    },
    icon: <LockIcon />,
    hasDivider: true,
  },
  {
    id: 'unlock',
    onClick: ({ globalDispatch, activeWidgetIds }) => {
      if (!globalDispatch) return;
      // can only unlock single widget
      if (!activeWidgetIds || activeWidgetIds.length > 1) return;

      globalDispatch(updateWidget({ widgetId: activeWidgetIds[0], widgetData: { isLocked: false } }));
    },
    label: 'unlock',
    checkIsHidden: ({ activeWidgetIds }) => {
      // Hidden for multiselect + group/reponsive widgets
      if (!activeWidgetIds || activeWidgetIds.length > 1) return true;

      const storeState = store.getState() as RootState;
      const widget = selectWidget(activeWidgetIds[0])(storeState);
      return !widget?.isLocked;
    },
    icon: <LockIcon />,
    hasDivider: true,
  },
  {
    id: 'group',
    label: 'group',
    onClick: groupActiveWidgets,
    icon: <GroupIcon />,
    shortcuts: {
      macOs: ['⌘', '+', '⌥', '+', 'G'],
      others: ['ctrl', '+', 'alt', '+', 'G'],
    },

    // Hidden if single or no widget selection
    checkIsHidden: ({ isMultiSelect }) => !isMultiSelect,
  },
  {
    id: 'ungroup',
    label: 'ungroup',
    icon: <GroupIcon />,
    onClick: unGroupActiveWidget,
    shortcuts: {
      macOs: ['⌘', '+', '⌥', '+', 'G'],
      others: ['ctrl', '+', 'alt', '+', 'G'],
    },

    // Hidden if multiple widgets are selected or a non-group widget is selected
    checkIsHidden: ({ isMultiSelect, isGroupWidget }) => isMultiSelect || !isGroupWidget,
  },
];

// Actions that are available when right click on canvas - not on widget
const CANVAS_ACTIONS: ContextMenuItem[] = [COMMON_ACTIONS.Paste];

export { WIDGET_ACTIONS, CANVAS_ACTIONS };
